import { Award, CalendarDays, PiggyBank } from 'lucide-react'
import { SAVINGS_TIER_LABELS, daysUntil, savingsTierFor, type Booklet } from '@shared/model'
import { Eyebrow, Meter } from '@/components/ui'
import { formatDate, formatDays, formatSek } from '@/lib/format'
import { cn } from '@/lib/cn'
import { CrestWatermark } from '@/components/Logo'

/**
 * Sparpanelen – appens gamification i ett kort.
 *
 * Poängen är inte poäng, utan att göra det konkret att häftet betalat sig.
 * Första nivån ligger därför exakt på vad häftet kostade: när mätaren är
 * full har man tjänat tillbaka sina pengar, och allt därefter är vinst.
 *
 * Panelen är medvetet hårt komprimerad på mobil. Den tog tidigare halva
 * skärmen och tryckte ned erbjudandena under vikningen – men häftets innehåll
 * är varför man öppnar appen, och sparbeloppet är belöningen man tittar på
 * efteråt. Belöningen får inte stå i vägen för varan.
 */
export function SavingsPanel({
  booklet,
  totalSavedSek,
  redemptionCount,
  offerCount,
  bookletValueSek,
}: {
  booklet: Booklet
  totalSavedSek: number
  redemptionCount: number
  offerCount: number
  /** Summan av alla erbjudanden i häftet, som andelen räknas mot. */
  bookletValueSek: number
}) {
  const tier = savingsTierFor(totalSavedSek, booklet.savingsTiers)
  const { small, medium, large } = booklet.savingsTiers

  const nextThreshold =
    totalSavedSek < small
      ? small
      : totalSavedSek < medium
        ? medium
        : totalSavedSek < large
          ? large
          : null

  const progress = nextThreshold
    ? Math.min(100, Math.round((totalSavedSek / nextThreshold) * 100))
    : 100

  const daysLeft = daysUntil(booklet.endDate)
  const expired = booklet.status === 'expired'
  const upcoming = booklet.status === 'upcoming'

  /*
   * Andelen av häftets hela värde som är uttagen.
   *
   * Taket på 100 % finns för att schablonvärdena är uppskattningar – handlar
   * man dyrare än vi räknat med kan sparbeloppet gå förbi summan, och "112 %
   * av häftet" läser som ett fel även när siffran är sann.
   *
   * Golvet är viktigare: den som sparat sextio kronor på ett häfte värt
   * femtusen ska inte mötas av "0 %", som säger att ingenting hänt.
   */
  const usedShare =
    bookletValueSek > 0
      ? Math.min(100, Math.round((totalSavedSek / bookletValueSek) * 100))
      : null
  const shareLabel =
    usedShare === null ? null : usedShare === 0 && totalSavedSek > 0 ? '<1' : String(usedShare)

  return (
    <section
      className="relative overflow-hidden rounded-card bg-brand-surface px-4 py-3.5 text-on-brand shadow-card sm:px-5 sm:py-4"
      aria-label="Ditt sparande"
    >
      {/* Klubbmärket som vattenstämpel, nedtonat till en antydan. Litet, för
          att kortet är lågt och ett avskuret märke läser som en fläck. */}
      <CrestWatermark width={72} className="top-2.5 right-4 rotate-[8deg] opacity-[0.11]" />

      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Eyebrow tone="accent" icon={<PiggyBank size={13} aria-hidden />}>
            {expired ? 'Du sparade' : 'Du har sparat'}
          </Eyebrow>
          <p className="mt-0.5 font-display text-3xl leading-none font-extrabold tracking-tight tabular-nums sm:text-4xl">
            {formatSek(totalSavedSek)}
          </p>
          {/* Sätter beloppet i förhållande till vad häftet är värt totalt.
              Dämpad text – siffran ovanför är radens blickfång. */}
          {shareLabel !== null && (
            <p className="mt-1 text-xs text-on-brand/70">
              {shareLabel} % av häftets värde, {formatSek(bookletValueSek)}
            </p>
          )}
        </div>

        {/* Kommande häften räknade tidigare ned mot slutdatumet, som om de
            redan gällde. Nu står det när de öppnar i stället. */}
        <p
          className={cn(
            'inline-flex shrink-0 items-center gap-1.5 rounded-pill px-2.5 py-1.5 text-xs font-semibold',
            expired || upcoming
              ? 'bg-white/15 text-on-brand/85'
              : daysLeft <= booklet.reminderDaysBefore
                ? 'bg-accent text-on-accent'
                : 'bg-white/15',
          )}
        >
          <CalendarDays size={14} aria-hidden />
          {upcoming
            ? `Öppnar ${formatDate(booklet.startDate)}`
            : expired
              ? `Gick ut ${formatDate(booklet.endDate)}`
              : daysLeft === 0
                ? 'Sista dagen idag'
                : `${formatDays(daysLeft)} kvar`}
        </p>
      </div>

      <div className="relative mt-3">
        <div className="mb-1 flex items-baseline justify-between gap-2 text-[11px] font-semibold">
          <span className="inline-flex items-center gap-1 text-accent">
            <Award size={13} aria-hidden />
            {SAVINGS_TIER_LABELS[tier]}
          </span>
          <span className="text-on-brand/70">
            {nextThreshold
              ? `${formatSek(nextThreshold - totalSavedSek)} till nästa nivå`
              : 'Högsta nivån nådd'}
          </span>
        </div>
        <Meter
          percent={progress}
          tone="accent"
          size="sm"
          onBrand
          label="Väg till nästa sparar-nivå"
        />
      </div>

      {/* Antalet är trevligt att veta men inte värt höjd på en liten skärm. */}
      <p className="relative mt-2 hidden text-xs text-on-brand/70 sm:block">
        {redemptionCount === 0
          ? `${offerCount} erbjudanden väntar på dig`
          : `${redemptionCount} av ${offerCount} erbjudanden använda`}
      </p>
    </section>
  )
}
